import { type Html, html, rune, View } from 'rune-ts';
import klass from './QuestionSection.module.scss';
import { typoPcMo } from '../../../../../shared/typography/typo';
import {
  makeFaqLinkIcon,
  makeMinusIcon,
  makePlusIcon,
} from '../../../../../shared/components/atoms/Icon/landing_icons';
import { isNull, map, pipe, throwIf, toArray } from '@fxts/core';

interface QuestionCardProps {
  question: string;
  answer_body: Html;
}

class QuestionCard extends View<QuestionCardProps> {
  override template({ question, answer_body }: QuestionCardProps): Html {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <div class="${klass.question_item}">
        <div class="${klass.question_title_wrapper}">
          <div class="${typoPcMo({ is_mobile, pc: '24_bold', mo: '14_bold' })}">${question}</div>
          <div class="${klass.toggle_status}">
            ${makePlusIcon({ klass: klass.closed })} ${makeMinusIcon({ klass: klass.opened })}
          </div>
        </div>
        <div class="${klass.answer_body} ${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
          <div class="${klass.answer_wrapper}">${answer_body}</div>
        </div>
      </div>
    `;
  }

  override onMount() {
    this.delegate('click', `.${klass.question_title_wrapper}`, () => {
      this.element().classList.toggle(`${klass.is_open}`);
      const answer_body = pipe(
        this.element().querySelector<HTMLElement>(`.${klass.answer_body}`),
        throwIf(isNull),
      );
      const answer_wrapper = pipe(
        this.element().querySelector<HTMLElement>(`.${klass.answer_wrapper}`),
        throwIf(isNull),
      );
      answer_body.style.height = this.element().matches(`.${klass.is_open}`)
        ? `${answer_wrapper.clientHeight}px`
        : '0px';
    });
  }
}

export class QuestionSection extends View {
  faq_data: QuestionCardProps[];

  constructor() {
    super({});
    this.faq_data = [
      {
        question: '왜 마플샵을 선택해야 하나요?',
        answer_body: html`
          <p>
            마플샵은 누구나 손쉽게 굿즈를 판매할 수 있는 커머스 플랫폼입니다.<br />
            현재 <span class="${klass.bold}">210만 명 이상의 회원</span>과
            <span class="${klass.bold}">8만 명 이상의 크리에이터</span>
            와 함께하고 있습니다. (2024년 10월 기준)
          </p>
          <p>
            크리에이터가 호스팅 비용 없이도 글로벌 커머스를 구축하고 유튜브 쇼핑을 연결해 다양한 방식으로 팬과
            소통할 수 있도록 돕습니다. <br />
            크리에이터가 상품 디자인만 하면 제작/배송/재고관리/고객응대까지 마플샵이 책임지는 마플샵제작배송
            서비스를 제공하여 크리에이터의 IP와 브랜드를 견고히 확장할 수 있는 기회를 제공합니다.
          </p>
          <p>
            단순한 판매 플랫폼이 아닌 유튜브 등 다양한 채널과 연동해 크리에이터가 팬들과 더욱 활발히 소통하고,
            브랜드를 성장시킬 수 있는 생태계를 제공합니다.<br />
            특히, 무재고 방식으로 제작이 가능하여 초기 비용이나 재고 관리 부담이 없어 누구나 부담 없이 시작할
            수 있습니다.<br />
            또한 입점 크리에이터들에게는 마플샵의 마케팅 지원, 온라인·오프라인 팝업스토어 지원 등의 혜택이
            제공되어 브랜드의 노출 기회가 한층 확대됩니다.
          </p>
        `,
      },
      {
        question: '마플샵을 개설하려면 어떻게 해야하나요?',
        answer_body: html`
          <p>
            마플샵은 개인, 기업 등에 상관없이 누구나 마플샵을 운영하여 수익을 창출할 수 있습니다.<br />
            크리에이터 신청 시 자신의 콘텐츠가 담긴 포트폴리오, 홈페이지, 유튜브, 인스타그램, 페이스북 등
            판매할 콘텐츠와 관련하여 심사과정에서 참고할 수 있는 자료나 웹사이트를 알려주세요.<br />
            제공해주신 정보를 기반으로 심사가 진행되며, 콘텐츠 관련 정보의 양이 많고 구체적일수록 심사에
            도움이 됩니다.
          </p>
          <p>
            크리에이터 신청 후 심사 기간은 영업일 기준 2~5일 정도 소요됩니다. <br />
            선정되신 분들께는 이메일로 마플샵 입점에 필요한 가이드북을 이메일로 보내드립니다.
          </p>
        `,
      },
      {
        question: '마플샵에서 판매 활동 시 수수료가 있나요?',
        answer_body: html`
          <p>상품의 유형에 따라 무료인 경우도, 소정의 수수료가 발생하는 경우도 있습니다.</p>
          <p>
            <span class="${klass.bold}">마플샵제작배송 상품</span><br />
            크리에이터가 보유한 디자인만으로 간편하게 상품을 제작하고 재고없이 판매할 수 있습니다.<br />
            <span class="${klass.bold}">* 수수료 무료</span>
          </p>
          <p>
            <span class="${klass.bold}">마플샵배송 상품</span><br />
            크리에이터의 상품을 마플샵에 입고시켜, 배송과 고객응대를 위탁합니다.<br />
            <span class="${klass.bold}">* 수수료 별도 협의</span>
          </p>
          <p>
            <span class="${klass.bold}">크리에이터배송 상품</span><br />
            크리에이터가 직접 제작하거나 보유하고 있는 상품을 판매하고 배송합니다.<br />
            <span class="${klass.bold}">* 결제 건 당 3.3% 판매 수수료</span>
          </p>
          <p>
            <span class="${klass.bold}">디지털/전자책</span><br />
            배경화면, 보이스팩, 전자책 등 다양한 디지털 파일을 등록하여 판매할 수 있습니다.<br />
            <span class="${klass.bold}">* 결제 건 당 12.5% 판매 수수료</span>
          </p>
          <div>
            <p>※ PG사 수수료 (3.52%)는 별도입니다.</p>
            <p>※ 마플샵 수수료 정책은 수시 변경될 수 있습니다.</p>
          </div>
          <p>
            <a
              href="https://www.marppleshop.help/5cd83306-749f-4c98-af61-b24589d53152"
              target="_blank"
              class="youtube-bridge-faq__a"
              >정산 FAQ 바로가기${makeFaqLinkIcon({ klass: klass.link_icon })}</a
            >
          </p>
        `,
      },
      {
        question: '마플샵에서 무엇을 판매할 수 있나요?',
        answer_body: html`
          <p>
            크리에이터가 직접 만든 핸드메이드 상품부터 시작해 디자인만 있어도 만들 수 있는 굿즈를 제작하고
            판매할 수 있습니다.<br />
            또한 디지털 콘텐츠, 교육 자료 등 다양한 상품을 통해 창작의 영역을 넓힐 수 있습니다.
          </p>
          <p>
            <span class="${klass.bold}">마플샵제작배송 상품</span> <br />
            디자인만 있다면 판매부터 제작, 배송, CS까지 모두 마플샵이 대신해 드리는 상품입니다.<br />
            주문 제작 방식(POD : Print on Demand)으로 마플샵에서 직접 상품을 제작해 발송, CS까지 진행합니다.
          </p>
          <p>
            <span class="${klass.bold}">마플샵배송 상품</span> <br />
            크리에이터가 보유한 상품을 마플샵에 입고시키고, 배송과 고객응대를 마플샵에 위탁하여 판매할 수
            있습니다.
          </p>
          <p>
            <span class="${klass.bold}">크리에이터배송 상품</span> <br />
            핸드메이드로 직접 제작한 상품이나 이미 제작을 완료하여 재고를 보유한 상품을 판매할 수 있습니다.<br />
            주문접수, 재고 관리, 배송 관리, 고객응대는 크리에이터가 직접 마플샵 스튜디오에서 진행할 수
            있습니다.
          </p>
          <p>
            <span class="${klass.bold}">디지털/전자책</span> <br />
            구매자가 온라인에서 다운로드하여 사용할 수 있는 상품입니다.<br />
            전자책, 온라인 클래스, 도안, 배경화면 이미지, 보이스 팩 등 다양한 형식의 디지털 상품을 판매할 수
            있습니다.
          </p>
        `,
      },
      {
        question: '마플샵에서 판매되면 어떻게 정산받나요?',
        answer_body: html`
          <p>
            마플샵에서 판매된 상품에 대한 정산은
            <span class="${klass.bold}">매월 1일 업데이트</span>됩니다.<br />
            전월 1일부터 말일까지의 정산 금액이 마플샵 스튜디오의 정산 관리 > 출금 신청 페이지에 반영되며,
            최소 출금 가능 금액은 1만 원입니다.
          </p>
          <p>
            출금 신청 가능 기간은 매월 1일부터 10일까지로 이 기간에 신청한 금액은 당월 20일에서 25일 사이에
            입금됩니다.<br />
            신청 마감 후인 매월 11일부터 말일까지의 신청 건은 다음 달로 자동 이월되어 다음 달에 입금됩니다.
          </p>
          <p>
            <a
              href="https://www.marppleshop.help/5cd83306-749f-4c98-af61-b24589d53152"
              target="_blank"
              class="youtube-bridge-faq__a"
              >정산 FAQ 바로가기${makeFaqLinkIcon({ klass: klass.link_icon })}</a
            >
          </p>
        `,
      },
      {
        question: '배송은 어떻게 하나요?',
        answer_body: html`
          <p>
            크리에이터배송 상품의 주문이 접수되었을 때 마플샵 스튜디오의 상품 판매 > 주문 관리에서 신규 주문을
            확인할 수 있습니다.<br />신규 주문의 상세 정보(주문, 수령인, 배송지 등) 확인 후
            <span class="${klass.bold}">상품 발송은 직접 준비해주세요.</span><br />
            배송 관리 페이지에서 주문을 확인하고 준비하는 ‘상품준비중’ 단계를 거쳐, 운송장 정보 입력과 함께
            ‘배송중’ 단계로 변경할 수 있습니다.
          </p>
          <p>배송 관리 페이지에서 택배사와 운송장 정보 입력 시, 구매자에게 배송 출발 알림이 발송됩니다.</p>
        `,
      },
    ];
  }

  override template() {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <section class="${klass.question}">
        <div class="${klass.title} ${typoPcMo({ is_mobile, pc: '48_bold', mo: '28_bold' })}">
          자주 묻는 질문 모아봤어요
        </div>
        <div class="${klass.question_list}">${this._renderQuestionCardList()}</div>
      </section>
    `;
  }

  private _renderQuestionCardList() {
    return pipe(
      this.faq_data,
      map((faq) => new QuestionCard(faq)),
      toArray,
    );
  }
}
